import type { Ref } from 'vue'
import { computed, unref } from 'vue'
import FieldStateData = App.Data.Fields.FieldStateData

export type FieldState = App.Fields.FieldState

export type FieldStateMap = Record<string, FieldStateData>

/** The value states carry their own field recipe; `filled` and the base state stay bare. */
export const fieldStateClass = (state?: FieldState | null): string => {
  switch (state) {
    case 'missing':
      return 'finest-field--missing'
    case 'reading':
      return 'finest-field--reading'
    case 'auto':
      return 'finest-field--auto'
    default:
      return ''
  }
}

/** Forms without a persisted entity behind them derive the state from the value they hold. */
export const missingWhen = (condition: boolean): FieldState | undefined => (condition ? 'missing' : undefined)

export const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === ''

export interface FieldPresentation {
  required: boolean
  state: FieldState | null
  source: string | null
  hint: string | null
}

const unknownField: FieldPresentation = { required: false, state: null, source: null, hint: null }

export function useFieldStates(source: Ref<FieldStateMap | undefined> | (() => FieldStateMap | undefined)) {
  const states = computed<FieldStateMap>(() => (typeof source === 'function' ? source() : unref(source)) ?? {})

  const presentationOf = (key: string): FieldPresentation => {
    const field = states.value[key]

    return field === undefined
      ? unknownField
      : { required: field.required, state: field.state, source: field.source, hint: field.hint }
  }

  const isMissing = (key: string): boolean => states.value[key]?.state === 'missing'

  const missingKeys = computed<string[]>(() =>
    Object.values(states.value)
      .filter((field) => field.state === 'missing')
      .map((field) => field.key),
  )

  const missingCountIn = (keys: readonly string[]): number =>
    missingKeys.value.filter((key) => keys.includes(key)).length

  return { states, presentationOf, isMissing, missingKeys, missingCountIn }
}
